import React, { useEffect } from "react";
import { useAuth } from "../context/AuthContext.js";
import { Loader2, ArrowLeft } from "lucide-react";

export interface AdminRouteProps {
  children: React.ReactNode;
  onRedirectHome?: () => void;
}

/**
 * Route-Level Frontend RBAC Guard for Platform Administration
 * Enforces strict role verification:
 * 1. If unauthenticated (!user): immediately redirects to home and prompts sign-in.
 * 2. If authenticated with role !== 'ADMIN': renders a clean 404 Not Found page
 *    without rendering admin metrics, moderation queues, or financial liabilities.
 * 3. If authenticated with role === 'ADMIN': grants access to children.
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children, onRedirectHome }) => {
  const { user, profile, isLoading, setIsAuthModalOpen } = useAuth();

  const isAdmin = Boolean(
    user && (
      (user as any).role === "ADMIN" ||
      profile?.role === "ADMIN" ||
      user.user_metadata?.role === "ADMIN" ||
      user.app_metadata?.role === "ADMIN"
    )
  );

  useEffect(() => {
    if (!isLoading && !user) {
      setIsAuthModalOpen(true);
      if (onRedirectHome) {
        onRedirectHome();
      } else {
        try {
          window.history.replaceState(null, "", "/");
        } catch {}
      }
    }
  }, [isLoading, user, onRedirectHome, setIsAuthModalOpen]);

  // 1. Authentication Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-8 space-y-3">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        <span className="text-xs text-zinc-400 font-mono">Verifying authorization credentials...</span>
      </div>
    );
  }

  // 2. Unauthenticated: Redirect to login/home (renders null during redirect)
  if (!user) {
    return null;
  }

  // 3. Authenticated but Unauthorized (role !== 'ADMIN'): Clean 404 Not Found
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4 font-sans select-none">
        <div className="w-20 h-20 rounded-3xl bg-zinc-900 border border-zinc-800 text-zinc-600 flex items-center justify-center mx-auto text-2xl font-mono font-bold shadow-2xl">
          404
        </div>
        <div className="space-y-1.5">
          <h2 className="text-xl font-bold text-white tracking-tight">This page isn&apos;t available</h2>
          <p className="text-xs text-zinc-400 max-w-sm mx-auto">
            The link you followed may be broken, or the page may have been removed or does not exist.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (onRedirectHome) {
              onRedirectHome();
            } else {
              try {
                window.history.pushState(null, "", "/");
                window.dispatchEvent(new PopStateEvent("popstate"));
              } catch {}
            }
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs shadow-lg shadow-emerald-500/20 active:scale-95 transition-all cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Go to Home</span>
        </button>
      </div>
    );
  }

  // 4. Authorized Platform Administrator
  return <>{children}</>;
};

export default AdminRoute;
